/**
 * 진행 규칙 — **에피소드가 바뀌어도 이건 그대로다.**
 *
 * 크라임씬의 판은 늘 같은 모양으로 돈다.
 *
 *   2인씩 조사 → 토론 → 추가 조사 → 마지막 조사 → 토론 → 투표
 *
 * 그 뼈대를 데이터(누가 어디에 무엇을 숨겼는가)에서 갈라 놓은 것이 이 파일이다.
 * 사흘도 초치성도 같은 규칙을 쓴다 — 다른 것은 방과 단서뿐이다.
 *
 * ## 왜 갈랐나
 *
 * 규칙이 레벨 파일 안에 살면 에피소드를 하나 더 만들 때마다 **같은 로직을 베끼게 된다.**
 * 백 개를 만들 계획이라면 그건 백 번 베끼고 백 번 어긋난다는 뜻이다.
 *
 * 앞으로 규칙이 다른 에피소드가 나오면(라운드가 더 길거나, 투표가 없거나)
 * `ROUNDS` 만 자기 것으로 덮어쓰면 된다 — 아래 함수들은 라운드 배열을 인자로 받는다.
 */

export interface RoundDef {
  id: number;
  name: string;
  /** 갈 수 있는 구역 */
  zones: readonly string[];
  /** 한 사람이 이 라운드에 쓸 수 있는 조사 횟수 */
  budget: number;
  /**
   * 필름 — 이 라운드에 찍을 수 있는 장수. 찍은 것만 칠판에 걸 수 있다.
   * 지정 안 하면 무제한. **난이도의 첫 손잡이다** — 무제한 86% → 3장 61% (sim/vote.py).
   */
  film?: number;
  /**
   * 이 라운드 뒤 브리핑에서 한 사람이 칠판에 걸 수 있는 장수.
   * 지정 안 하면 서버 기본값(3). 마지막 라운드를 조이면
   * 「다 찾았는데 다 못 보여준」 긴장이 투표 직전까지 간다.
   */
  pinLimit?: number;
  minutes: number;
  /** 탐정이 구역을 배정하는 라운드인가 (권고이지 봉쇄가 아니다) */
  assign: boolean;
  /** squad — 한 조씩 나갔다 온다 · free — 전원 동시 · solo — 한 사람씩 */
  mode: 'squad' | 'free' | 'solo';
  /** 한 차례에 나가는 인원 */
  perTurn: number;
}

/**
 * 표준 3라운드.
 *
 * 1차는 **2인 1조**로 나간다 — 둘이 같이 봐야 「누가 무엇을 봤나」가 증언으로 갈리고,
 * 그 어긋남이 토론의 재료가 된다. 혼자 보내면 아무도 반박할 수 없다.
 *
 * 2차는 전원 동시다. 1차 토론에서 나온 의심을 각자 확인하러 가는 시간이라
 * 짝을 묶으면 오히려 방해가 된다.
 *
 * 마지막은 한 사람씩, 예산 넷. **짧아야 한다** — 여기서 판을 뒤집을 결정타를
 * 찾게 하면 앞의 두 라운드가 무의미해진다.
 */
export function standardRounds(zones: readonly string[]): RoundDef[] {
  return [
    { id: 1, name: '1차 조사', zones, budget: 8, minutes: 12, assign: true, mode: 'squad', perTurn: 4 },
    { id: 2, name: '2차 조사', zones, budget: 10, minutes: 10, assign: false, mode: 'free', perTurn: 10 },
    { id: 3, name: '마지막 조사', zones, budget: 4, minutes: 5, assign: false, mode: 'solo', perTurn: 2 },
  ];
}

/** 라운드 번호를 정의로. 범위를 벗어나면 양 끝으로 문다 */
export function roundDefOf(rounds: RoundDef[], n: number): RoundDef {
  return rounds[Math.max(0, Math.min(rounds.length - 1, n - 1))];
}

/**
 * 라운드 판정 세 가지 — **ROUNDS 를 주입받는 형태.**
 *
 * 같은 로직이 `level-sahol.ts` 에도 있다. 그쪽은 자기 `ROUNDS` 를 모듈 스코프에서
 * 직접 보므로 **사흘 말고 다른 에피소드에는 못 쓴다.** 방마다 다른 사건을 돌리게
 * 되면서 그 결합을 풀어야 했는데, 사흘 쪽 함수를 고치면 이미 도는 판이 흔들린다.
 * 그래서 새 에피소드는 이쪽을 쓴다.
 *
 * (사흘도 언젠가 이쪽으로 넘어와야 한다. 그때 저쪽 세 함수가 사라진다.)
 */
export function roundGuards(rounds: RoundDef[]) {
  const at = (n: number) => roundDefOf(rounds, n);
  return {
    investigateBlock(
      round: number,
      prop: { room: string; fromRound?: number; lateReason?: string },
      _assignedZone: string | undefined,
      budgetLeft: number | undefined,
    ): string | null {
      if ((prop.fromRound ?? 1) > round) return prop.lateReason ?? '아직은 손댈 수 없다.';
      if ((budgetLeft ?? at(round).budget) <= 0) return '이번 라운드 조사 횟수를 다 썼습니다.';
      return null;
    },

    squadBlock(round: number, out: string[], done: string[], playerId: string): string | null {
      const r = at(round);
      if (out.includes(playerId)) return null;
      if (r.mode === 'free') return out.length ? null : '아직 조사가 시작되지 않았습니다.';
      if (done.includes(playerId)) return '이번 라운드 조사는 이미 다녀왔습니다.';
      if (!out.length) {
        return r.mode === 'solo'
          ? '아직 안 나갔습니다. O 를 눌러 나가세요.'
          : '아직 안 나갔습니다. O 로 같이 갈 사람을 정하세요 — 혼자면 F8.';
      }
      return '다른 조가 나가 있습니다. 돌아올 때까지 기다리세요.';
    },

    roundDef: at,
  };
}

/** 서로 지목했고 둘 다 아직 안 다녀온 짝. 라운드와 무관해서 그대로 공유한다 */
export function readyPairOf(
  ids: string[],
  done: string[],
  pairing: Record<string, string>,
): [string, string] | null {
  for (const a of ids) {
    if (done.includes(a)) continue;
    const b = pairing[a];
    if (!b || b === a || done.includes(b) || !ids.includes(b)) continue;
    if (pairing[b] === a) return [a, b];
  }
  return null;
}
